<script setup lang="ts">
import { AllComponents } from "@/builder/client/AllComponents";
import { FieldStyle, PageType, PartialDisplayType } from "@/builder/enums";
import { PageDesign } from '@/builder/interfaces';
import { useRenderStore } from "@/builder/stores/renderStore";
import StretchyDots from "@/components/shared/animation/StretchyDots.vue";
import TestModeBanner from '@/components/shared/banner/TestModeBanner.vue';
import { computed, defineAsyncComponent, inject, nextTick, onBeforeMount, onMounted, provide, ref, watch } from "vue";
import { publicApiService } from "../../services/publicApiService";
import ErrorContainer from "../components/shared/ErrorContainer.vue";

//asyc components
const CookieConsent = defineAsyncComponent(() => import("../../components/shared/cookie/CookieConsent.vue"));

// props
const props = defineProps<{
    pageId: string;
    organizationId: string;
    environment: string;
}>();

if (props.environment === "local") {
    //delete the div tag with id #app to avoid odd spacing when testing locally
    const appDiv = document.getElementById("app");
    if (appDiv) {
        appDiv.remove();
    }
}

// inject
const renderStore = useRenderStore();
const publicApi = inject('publicApiService') as publicApiService;

const pageDesign = ref(renderStore.page.pageDesign ?? {
    fieldStyle: FieldStyle.Default
} as PageDesign);

const showCookieConsent = ref(false);

const updatePageDesign = (newVal: PageDesign) => {
    pageDesign.value = newVal;
}

provide("pageDesign", pageDesign);

// refs
const organizationId = ref(Number.parseInt(props.organizationId));

// computed
const isEmbed = computed(() => renderStore.page.pageType !== PageType.Full);
const getRaiseDonorsClass = computed(() => {
    return isEmbed.value ? "raise-donors raise-donors-embed" : "raise-donors";
});

// lifecycle
onBeforeMount(async () => {
    renderStore.loadOrganization(organizationId.value);
    await renderStore.load(props.pageId, organizationId.value);
    updatePageDesign(renderStore.page.pageDesign);
});

onMounted(async () => {
    // @ts-ignore
    if (window.zE) {
        // @ts-ignore
        window.zE("webWidget", "hide");
    }

    // If user is using tracking pixel, get visitor giving
    if (renderStore.page.useTrackingPixel) {
        try {
            renderStore.visitorGiving = await publicApi.findVisitor(organizationId.value);
        } catch (error) {
            renderStore.visitorGiving = null;
        }
    }
});

// watch
watch(() => renderStore.cookieAcceptance, (newVal) => {
    //wait for next tick to show cookie consent so we do not show it before properly set
    nextTick(() => {
        if (newVal) {
            showCookieConsent.value = !newVal.accepted;
        }
    });
});
</script>

<template>
    <template v-if="isEmbed">
        <div id="raise-donors" :class="getRaiseDonorsClass">
            <TestModeBanner v-if="renderStore.page.partialDisplayType !== PartialDisplayType.Modal" source="public" />
            <ErrorContainer v-if="renderStore.page.partialDisplayType !== PartialDisplayType.Modal" />
            <template v-for="(component) in renderStore.page.components" :key="component.id">
                <component :is="AllComponents[component.type]" :component="component" />
            </template>
            <StretchyDots v-if="renderStore.isProcessing" />
        </div>
    </template>

    <template v-else>
        <main id="raise-donors" :class="getRaiseDonorsClass">
            <TestModeBanner v-if="renderStore.page.partialDisplayType !== PartialDisplayType.Modal" source="public" />
            <ErrorContainer v-if="renderStore.page.partialDisplayType !== PartialDisplayType.Modal" />
            <template v-for="(component) in renderStore.page.components" :key="component.id">
                <component :is="AllComponents[component.type]" :component="component" />
            </template>
            <StretchyDots v-if="renderStore.isProcessing" />
            <!-- <CookieConsent v-if="showCookieConsent" :backgroundColor="pageDesign?.backgroundColor ?? undefined"
                :font-color="pageDesign?.bodyTextColor ?? undefined" :organization-name="renderStore.organization?.name"
                @cookie-acceptance="renderStore.setCookieAcceptance" /> -->
        </main>
    </template>
</template>

<style lang="scss">
@import "@/builder/assets/sass/pages.scss";
</style>
